// @ts-strict-ignore
import { ReportGenerationPropertyVO } from '../components/article/model/vo/property.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { ProjectType } from '../model/project-type';
import { ReportGenerationPropertySetTypesDef } from '@project/components/project/constants/report-generation-property-set-type-def';
import { ReportGenerationPropertyDef } from '@project/components/project/constants/report-generation-property-def';
import { ReportIdDef } from '@project/components/project/constants/report-id-def';
import { CameraParametersVO } from '@3d-scene/camera-parameters.vo';
import { SceneManager } from '@3d-scene/manager/scene.manager';

export class ReportGenerationPropertyUtils {

  public static readonly ISOMETRIC_CAMERA_SETTINGS: string = 'isometric_camera_settings';
  public static readonly CUSTOM_CAMERA_SETTINGS: string = 'custom_camera_settings';
  public static readonly PROJECT_3D_REPORT_SUBTYPES: string[] =
    [ReportGenerationPropertyUtils.ISOMETRIC_CAMERA_SETTINGS, ReportGenerationPropertyUtils.CUSTOM_CAMERA_SETTINGS];

  public static updateReportGenerationProperties(userProperties: ReportGenerationPropertyVO[],
    newProperties: ReportGenerationPropertyVO[]): void {
    if (!userProperties || ArrayUtils.isEmpty(newProperties)) {
      return;
    }
    for (const newProperty of newProperties) {
      if (newProperty.transientProperty) {
        continue;
      }

      const userProperty: ReportGenerationPropertyVO = this.getPropertyByNameAndSetType(
        userProperties, newProperty.name, newProperty.setType, newProperty.reportId);
      if (userProperty) {
        userProperty.values = newProperty.values;
        userProperty.transientProperty = newProperty.transientProperty;
      } else {
        userProperties.push(newProperty);
      }
    }
  }

  public static getPropertyByNameAndSetType(properties: ReportGenerationPropertyVO[], propertyName: string, setType: string,
    reportId: string): ReportGenerationPropertyVO {
    if (ArrayUtils.isEmpty(properties) || !propertyName) {
      return null;
    }
    for (let i = 0; i < properties.length; i++) {
      const property: ReportGenerationPropertyVO = properties[i];
      if (property && property.name === propertyName && property.setType === setType && property.reportId === reportId) {
        return property;
      }
    }
    return null;
  }

  public static setBooleanPropertyValue(properties: ReportGenerationPropertyVO[], propertyName: string, booleanValue: boolean,
    setType: string, overwriteExistingValues: boolean = true, isTransientProperty: boolean = false, reportId: string = null): void {
    if (!properties) {
      return;
    }
    const newProperty: ReportGenerationPropertyVO = this.addPropertyIfNotExists(
      properties, propertyName, setType, isTransientProperty, reportId);
    PropertyUtils.setBooleanPropertyValue([newProperty], propertyName, booleanValue, overwriteExistingValues, isTransientProperty);
  }

  public static setStringPropertyValue(properties: ReportGenerationPropertyVO[], propertyName: string, stringValue: string,
    setType: string, overwriteExistingValues: boolean = true, isTransientProperty: boolean = false, reportId: string = null): void {
    if (!properties) {
      return;
    }
    const newProperty: ReportGenerationPropertyVO = this.addPropertyIfNotExists(
      properties, propertyName, setType, isTransientProperty, reportId);
    PropertyUtils.setStringPropertyValue([newProperty], propertyName, stringValue, overwriteExistingValues, isTransientProperty);
  }

  public static setStringPropertyValues(properties: ReportGenerationPropertyVO[], propertyName: string, values: string[], setType: string,
    isTransientProperty: boolean = false, reportId: string = null): void {
    if (ArrayUtils.isEmpty(properties) || ArrayUtils.isEmpty(values)) {
      return;
    }
    const newProperty: ReportGenerationPropertyVO = this.addPropertyIfNotExists(properties, propertyName, setType,
      isTransientProperty, reportId);
    for (const value of values) {
      PropertyUtils.setStringPropertyValue([newProperty], propertyName, value, false, isTransientProperty);
    }
  }

  public static setIntPropertyValue(properties: ReportGenerationPropertyVO[], propertyName: string, intValue: number, setType: string,
    overwriteExistingValues: boolean = true, isTransientProperty: boolean = false, reportId: string = null): void {
    if (!properties) {
      return;
    }
    const newProperty: ReportGenerationPropertyVO = this.addPropertyIfNotExists(
      properties, propertyName, setType, isTransientProperty, reportId);
    PropertyUtils.setIntPropertyValue([newProperty], propertyName, intValue, overwriteExistingValues, isTransientProperty);
  }

  private static addPropertyIfNotExists(properties: ReportGenerationPropertyVO[], propertyName: string, setType: string,
    isTransientProperty: boolean, reportId: string): ReportGenerationPropertyVO {
    let property: ReportGenerationPropertyVO = this.getPropertyByNameAndSetType(properties, propertyName, setType, reportId);
    if (!property) {
      property = new ReportGenerationPropertyVO();
      property.name = propertyName;
      property.setType = setType;
      property.reportId = reportId;
      property.transientProperty = isTransientProperty;
      properties.push(property);
    }
    return property;
  }

  public static createCameraParametersByProperties(reportProperties: ReportGenerationPropertyVO[], sceneManager: SceneManager,
    setType: string = null): CameraParametersVO {
    const subtype: string = this.getReportSubtype(reportProperties, ReportIdDef.PROJECT_3D_REPORT_ID, setType);
    return ReportGenerationPropertyUtils.ISOMETRIC_CAMERA_SETTINGS === subtype ?
      sceneManager.createDefaultIsometricCameraParameters() : sceneManager.createCurrentCameraParameters();
  }

  public static getSetTypeByProjectType(projectType: ProjectType): string {
    let setType: string;
    switch (projectType) {
      // TODO: (from flex) Bug 2932, temporary solution to support CIRCUIT_BOARD projects
      case "MOUNTING_RAIL":
      case "FREE_MOUNTING":
      case "CIRCUIT_BOARD":
      case "MCS":
      case "GDS":
      case "SPEEDWAY":
        setType = ReportGenerationPropertySetTypesDef.MOUNTING_RAIL;
        break;
      case "MARKINGSYSTEM":
        setType = ReportGenerationPropertySetTypesDef.MARKING_SYSTEM;
        break;
      case "XCOM":
        setType = ReportGenerationPropertySetTypesDef.XCOM;
        break;
      /*case WagoProjectType.CIRCUIT_BOARD:
      {
        setType = WagoReportGenerationPropertySetTypesDef.CIRCUIT_BOARD;
        break;
      }*/
      default:
        setType = ReportGenerationPropertySetTypesDef.DEFAULT;
        break;
    }
    return setType;
  }

  public static getPDFReportGenerationProperties(userReportGenerationProperties: ReportGenerationPropertyVO[],
    setType: string): ReportGenerationPropertyVO[] {
    if (ArrayUtils.isEmpty(userReportGenerationProperties)) {
      return [];
    }
    let properties: ReportGenerationPropertyVO[] = null;
    if (setType !== ReportGenerationPropertySetTypesDef.DEFAULT) {
      properties = this.getReportGenerationProperties(userReportGenerationProperties, setType);
    }
    return ArrayUtils.isNotEmpty(properties) ? properties :
      this.getReportGenerationProperties(userReportGenerationProperties, ReportGenerationPropertySetTypesDef.DEFAULT);
  }

  public static getCSVReportGenerationProperties(userReportGenerationProperties: ReportGenerationPropertyVO[])
    : ReportGenerationPropertyVO[] {
    return this.getReportGenerationProperties(userReportGenerationProperties, ReportGenerationPropertySetTypesDef.DEFAULT);
  }

  private static getReportGenerationProperties(userReportGenerationProperties: ReportGenerationPropertyVO[],
    setType: string): ReportGenerationPropertyVO[] {
    const properties: ReportGenerationPropertyVO[] = [];
    for (const property of userReportGenerationProperties) {
      if (property.setType === setType) {
        properties.push(property);
      }
    }
    return properties;
  }

  public static getReportScale(reportProperties: ReportGenerationPropertyVO[], reportId: string, setType: string): number {
    const property: ReportGenerationPropertyVO = ReportGenerationPropertyUtils.getPropertyByNameAndSetType(reportProperties,
      ReportGenerationPropertyDef.SCALE, setType, reportId);
    return property == null ? NaN : PropertyUtils.getIntPropertyValueByProperty(property);
  }

  public static getReportSubtype(reportProperties: ReportGenerationPropertyVO[], reportId: string, setType: string = null): string {
    const property: ReportGenerationPropertyVO = setType == null ?
      this.getPropertyByName(reportProperties, ReportGenerationPropertyDef.SUBTYPE, reportId) :
      ReportGenerationPropertyUtils.getPropertyByNameAndSetType(reportProperties, ReportGenerationPropertyDef.SUBTYPE, setType, reportId);
    return property == null ? null : PropertyUtils.getStringPropertyValueByProperty(property);
  }

  public static getPropertyByName(properties: ReportGenerationPropertyVO[], propertyName: string,
    reportId: String): ReportGenerationPropertyVO {
    if (!properties || !propertyName) {
      return null;
    }
    for (let i = 0; i < properties.length; i++) {
      const property: ReportGenerationPropertyVO = properties[i];
      if (property && property.name === propertyName && property.reportId === reportId) {
        return property;
      }
    }
    return null;
  }
}
